/*
 * Promo codes on the cart (snippets/bpp-cart-discount.liquid).
 *
 * Posts `discount` to the Cart AJAX API and redraws the cart through the same
 * section-rendering pass Taste's cart uses for a quantity change: the sections
 * come from the host <cart-drawer-items> / <cart-items> getSectionsToRender(),
 * so the drawer or the cart page updates exactly as it would for any other edit.
 *
 * Every request sends the full list of codes to keep. `discount` replaces the
 * cart's codes rather than appending to them, and "" clears them all.
 */
// The drawer and the cart page each load this file, so on /cart it runs twice.
// A top-level class declaration would throw on the second run.
if (!customElements.get('bpp-cart-discount')) {
  customElements.define(
    'bpp-cart-discount',
    class BppCartDiscount extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('.bpp-discount__input');
        this.button = this.querySelector('.bpp-discount__apply');
        this.errorNode = this.querySelector('.bpp-discount__error');
        if (!this.form || !this.input) return;

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.apply();
        });
        this.addEventListener('click', (event) => {
          const remove = event.target.closest('[data-bpp-discount-remove]');
          if (remove) this.remove(remove.dataset.bppDiscountRemove);
        });
        this.input.addEventListener('input', () => this.showError(''));
      }

      get applied() {
        return [...this.querySelectorAll('[data-bpp-discount-code]')].map((chip) => chip.dataset.bppDiscountCode);
      }

      get cartItems() {
        return this.closest('cart-drawer')
          ? document.querySelector('cart-drawer-items')
          : document.querySelector('cart-items');
      }

      async apply() {
        const code = this.input.value.trim();
        if (!code) {
          this.showError(this.dataset.errorEmpty);
          this.input.focus();
          return;
        }

        const kept = this.applied;
        if (kept.some((existing) => existing.toUpperCase() === code.toUpperCase())) {
          this.input.value = '';
          return;
        }

        this.setBusy(true);
        let state = await this.post([...kept, code]);
        if (!state) return;

        const match = (state.discount_codes || []).find(
          (discount) => String(discount.code).toUpperCase() === code.toUpperCase()
        );
        if (!match || !match.applicable) {
          // Shopify keeps an inapplicable code on the cart. Put the cart back to the
          // codes that do apply, then say why the new one was refused.
          state = await this.post(kept);
          if (!state) return;
          this.render(state);
          this.successor()?.fail(code, this.dataset.errorInvalid);
          return;
        }

        this.render(state);
        const next = this.successor();
        next?.announce(this.dataset.appliedText.replace('[code]', match.code));
        next?.restoreFocus();
        this.notify(state);
      }

      async remove(code) {
        this.setBusy(true);
        const state = await this.post(this.applied.filter((existing) => existing !== code));
        if (!state) return;

        this.render(state);
        const next = this.successor();
        next?.announce(this.dataset.removedText.replace('[code]', code));
        next?.restoreFocus();
        this.notify(state);
      }

      async post(codes) {
        const host = this.cartItems;
        const sections = host ? host.getSectionsToRender() : [];
        const body = JSON.stringify({
          discount: codes.join(','),
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
          const state = await response.json();
          if (!response.ok || state.status) throw new Error(state.description || state.message || response.status);
          return state;
        } catch (error) {
          console.error('bpp-cart-discount: cart update failed', error);
          this.setBusy(false);
          this.showError(this.dataset.errorFailed);
          return null;
        }
      }

      /* Same loop as CartItems.updateQuantity() in assets/cart.js. */
      render(state) {
        const host = this.cartItems;
        if (!host || !state.sections) return;

        host.getSectionsToRender().forEach((section) => {
          const html = state.sections[section.section];
          const container = document.getElementById(section.id);
          if (!html || !container) return;
          const target = container.querySelector(section.selector) || container;
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
          if (source) target.innerHTML = source.innerHTML;
        });
      }

      /* The render replaced this element; the fresh one carries on. */
      successor() {
        return document.querySelector(`bpp-cart-discount[data-context="${this.dataset.context}"]`);
      }

      /*
       * Other listeners (sticky cart bar, price-per-item) refresh from cartUpdate.
       * source 'cart-items' is Taste's own tag for "the cart UI already redrew
       * itself", so <cart-drawer-items> / <cart-items> skip their refetch and the
       * field keeps focus instead of being replaced a second time.
       */
      notify(state) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
        }
      }

      restoreFocus() {
        this.input.focus();
      }

      fail(code, message) {
        this.input.value = code;
        this.showError(message);
        this.input.focus();
        this.input.select();
      }

      announce(message) {
        const status = this.querySelector('[data-bpp-discount-status]');
        if (status) status.textContent = message;
      }

      showError(message) {
        if (!this.errorNode) return;
        this.errorNode.textContent = message || '';
        this.errorNode.hidden = !message;
        if (message) {
          this.input.setAttribute('aria-invalid', 'true');
        } else {
          this.input.removeAttribute('aria-invalid');
        }
        this.setBusy(false);
      }

      setBusy(busy) {
        this.toggleAttribute('aria-busy', busy);
        if (this.button) this.button.disabled = busy;
      }
    }
  );
}
